import React, { useEffect, useMemo } from 'react';
import { Button, Col, Form, InputNumber, Row, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Drawer } from '@/components/drawer';
import { DrawerSizeEnum, PlacementEnum } from '@/types';
import { REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { useProductList } from '@/features/product-configurator/hooks/productSetup';
import { useAllConfigOptions } from '@/features/product-configurator/hooks/configOption';
import { OPTION_TYPE_SECURITY, OPTION_TYPE_SPRAY, PROFILE_COLOR_OPTION } from '@/features/assistant/constants';
import { isProfileColourFoilOption } from '@/features/assistant/utils/profileColour';
import {
  OPTION_TYPE_EXTERNAL_PROFILE_COLOUR,
  OPTION_TYPE_INTERNAL_PROFILE_COLOUR,
  OPTION_TYPE_TIMBER_LOOK_JOINTS,
} from '../constants';
import { IBufferDaysRule, IProfileColourCombination } from '../types';

export interface IBufferDaysRuleFormValues {
  bufferDays: number;
  regions: number[];
  productIds: string[];
  /** Single value */
  profileColourTypeId?: string;
  /** Dynamic list of internal/external colour pairs — add-a-row form */
  colourCombinations?: IProfileColourCombination[];
  /** Single value — Spray's own options are literally "Yes"/"No" */
  sprayId?: string;
  /** Single value */
  timberLockId?: string;
  /** Single value */
  securityIds?: string[];
}

interface BufferDaysRuleDrawerProps {
  open: boolean;
  editingRule: IBufferDaysRule | null;
  onClose: () => void;
  isSaving?: boolean;
}

// Save/submit is wired by the parent — it wraps this Drawer in a <Form
// onFinish={...}>, same convention as VSSetupFormModal + OnboardingPage.
export const BufferDaysRuleDrawer: React.FC<BufferDaysRuleDrawerProps> = ({
  open,
  editingRule,
  onClose,
  isSaving = false,
}) => {
  const form = Form.useFormInstance<IBufferDaysRuleFormValues>();
  const selectedProfileColourTypeId: string | undefined = Form.useWatch('profileColourTypeId', form);
  const colourCombinations: IProfileColourCombination[] = Form.useWatch('colourCombinations', form) ?? [];

  const { data: productsData, isLoading: isProductsLoading } = useProductList({
    queryParams: { current: 1, pageSize: 200, filter: '' },
  });
  const { data: allOptionsData, isLoading: isOptionsLoading } = useAllConfigOptions();

  const productOptions = useMemo(
    () =>
      (productsData?.value ?? [])
        .filter((p: any) => p.statecode === 0)
        .map((p: any) => ({ value: p.productid, label: p.name })),
    [productsData],
  );

  const activeOptions = useMemo(
    () => (allOptionsData ?? []).filter((o: any) => o.statecode === 0),
    [allOptionsData],
  );

  const optionsByType = (type: number) =>
    activeOptions
      .filter((o: any) => o.dogma_optiontype === type)
      .map((o: any) => ({ value: o.dogma_configurationoptionsid, label: o.dogma_name }));

  const profileColourTypeOptions = useMemo(() => optionsByType(PROFILE_COLOR_OPTION), [activeOptions]);
  const internalColorOptions = useMemo(() => optionsByType(OPTION_TYPE_INTERNAL_PROFILE_COLOUR), [activeOptions]);
  const externalColorOptions = useMemo(() => optionsByType(OPTION_TYPE_EXTERNAL_PROFILE_COLOUR), [activeOptions]);
  const sprayOptions = useMemo(() => optionsByType(OPTION_TYPE_SPRAY), [activeOptions]);
  const timberLockOptions = useMemo(() => optionsByType(OPTION_TYPE_TIMBER_LOOK_JOINTS), [activeOptions]);
  const securityOptions = useMemo(() => optionsByType(OPTION_TYPE_SECURITY), [activeOptions]);

  // Internal/External Profile Colour only matter once a foil profile colour
  // type has been picked (e.g. "Crystal White Foil Both Sides") — a plain
  // "Standard White" selection has no internal/external colour to set.
  const isFoilSelected = useMemo(() => {
    if (!selectedProfileColourTypeId) return false;
    const label = profileColourTypeOptions.find((o) => o.value === selectedProfileColourTypeId)?.label;
    return isProfileColourFoilOption(label ?? '');
  }, [selectedProfileColourTypeId, profileColourTypeOptions]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(
      editingRule
        ? {
            bufferDays: editingRule.bufferDays,
            regions: editingRule.regions,
            productIds: editingRule.productIds,
            profileColourTypeId: editingRule.profileColourTypeId,
            colourCombinations: editingRule.colourCombinations,
            sprayId: editingRule.sprayId,
            timberLockId: editingRule.timberLockId,
            securityIds: editingRule.securityIds,
          }
        : {
            bufferDays: undefined,
            regions: [],
            productIds: [],
            profileColourTypeId: undefined,
            colourCombinations: [],
            sprayId: undefined,
            timberLockId: undefined,
            securityIds: [],
          },
    );
  }, [open, editingRule, form]);

  // Clear any colour combinations picked while a foil type was selected
  // once the profile colour type no longer includes a foil option.
  useEffect(() => {
    if (open && !isFoilSelected) {
      form.setFieldsValue({ colourCombinations: [] });
    }
  }, [isFoilSelected, open, form]);

  // Always show at least one colour-combination row once foil is selected —
  // the fields shouldn't require an extra "Add" click before appearing.
  useEffect(() => {
    if (open && isFoilSelected && colourCombinations.length === 0) {
      form.setFieldsValue({ colourCombinations: [{}] });
    }
  }, [open, isFoilSelected, colourCombinations.length, form]);

  return (
    <Drawer
      title={editingRule ? 'Edit Buffer Days Configuration' : 'New Buffer Days Configuration'}
      subTitle="Set a lead-time override for a specific product configuration. Leave a field empty to match any value."
      open={open}
      onClose={onClose}
      loading={isSaving}
      width={800}
      placement={PlacementEnum.right}
      size={DrawerSizeEnum.medium}
      saveBtnText={editingRule ? 'Update' : 'Save'}
    >
      <div style={{ marginTop: 24 }}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="bufferDays"
              label="Buffer Days"
              rules={[{ required: true, message: 'Please enter buffer days' }]}
            >
              <InputNumber min={0} max={90} addonAfter="working days" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="regions" label="Region">
              <Select
                mode="multiple"
                allowClear
                placeholder="Any region"
                options={REGION_FILTERS_OPTIONS}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="productIds" label="Product">
              <Select
                mode="multiple"
                allowClear
                placeholder="Any product"
                loading={isProductsLoading}
                options={productOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="profileColourTypeId" label="Profile Colour Type">
              <Select
                showSearch
                allowClear
                placeholder="Any profile colour type"
                loading={isOptionsLoading}
                options={profileColourTypeOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        {isFoilSelected && (
          <div className="mb-4">
            <Form.List name="colourCombinations">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      <Col xs={22} md={11}>
                        <Form.Item {...restField} name={[name, 'internalColorId']} label="Internal Profile Colour">
                          <Select
                            showSearch
                            allowClear
                            placeholder="Any internal colour"
                            loading={isOptionsLoading}
                            options={internalColorOptions}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={22} md={11}>
                        <Form.Item {...restField} name={[name, 'externalColorId']} label="External Profile Colour">
                          <Select
                            showSearch
                            allowClear
                            placeholder="Any external colour"
                            loading={isOptionsLoading}
                            options={externalColorOptions}
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={2} md={2} className="flex justify-center">
                        {fields.length > 1 && (
                          <MinusCircleOutlined onClick={() => remove(name)} className="text-red-500" />
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                    Add Colour Combination
                  </Button>
                </>
              )}
            </Form.List>
          </div>
        )}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="sprayId" label="Spray">
              <Select
                allowClear
                placeholder="Any"
                loading={isOptionsLoading}
                options={sprayOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="timberLockId" label="Timber Look Joints">
              <Select
                showSearch
                allowClear
                placeholder="Any"
                loading={isOptionsLoading}
                options={timberLockOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="securityIds" label="Security">
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Any"
                loading={isOptionsLoading}
                options={securityOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Drawer>
  );
};

export default BufferDaysRuleDrawer;
